import { Reveal } from "@/components/Reveal";

const SANDBOX = [
  ["Booked + weighted pending stacked, with a budget line, in one chart", "m", "Likely", "Two side-by-side reports on the same dashboard"],
  ["Market managers seeing only their own market", "m", "Needs testing", "One dashboard per market with fixed filters"],
  ["Nightly roll-up finishing within HubSpot's runtime and API limits", "m", "Needs testing", "Run the jobs on a small external scheduled function, at low cost"],
  ["Variance % at AE, market and corporate level", "ok", "Achievable", "Built with report formulas (total variance \u00f7 total budget), never an average of percentages"],
  ["Linked billing and forecast records on the budget record", "ok", "Achievable", "Optional - adds hours, so we'd decide whether it's worth it"],
];

const RISKS = [
  ["AEs not entering line items", "The forecast is only as good as what AEs put in. This is a behaviour change, not a build issue.", "h", "High", "A simple product library, short training, and a \u201cdeals missing line items\u201d report for managers. Needs backing from MMG sales leadership."],
  ["Budget data quality", "Template errors, and markets that can't budget at station level.", "h", "High", "Cleaned-up template, an exceptions report per market, and the whole pipeline tested on the 2026 file first."],
  ["Billing code mismatch", "We don't know its size until we see the data.", "m", "Medium", "Reviewed in week 1. If it's bigger than expected, we flag it with hours before going further."],
  ["Closed Won to WideOrbit handoff", "Projections can dip or double count around close.", "m", "Medium", "Agree a matching rule, or a hold period, before the forecast engine is built."],
  ["Decision and UAT turnaround", "The most likely reason for the timeline to slip.", "m", "Medium", "One working session in week 1, and a named decision-maker on the MMG side."],
  ["Scope changes mid-build", "", "m", "Medium", "Changes go through a short change request with hours attached, so nobody's surprised."],
];

const LVL = {
  h: "bg-[#F6DEDE] text-bad",
  m: "bg-[#F5E6CC] text-warn",
  ok: "bg-sunk text-fog",
};

export default function Risks() {
  return (
    <section id="risks" className="mx-auto max-w-6xl px-4 sm:px-6 mt-14 scroll-mt-16" data-testid="section-risks">
      <Reveal>
        <p className="font-mono text-[11.5px] uppercase tracking-[0.08em] text-fog">Risks</p>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl text-coal">
          What could get in the way, and what we'd do about it
        </h2>
        <p className="mt-2 text-[15px] text-fog max-w-3xl">
          We'd rather flag these now than find them halfway through. Most of the real risk
          sits in data and adoption, not in the build itself.
        </p>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-5 bg-white border border-hair rounded-md p-5" data-testid="sandbox-tests">
          <h3 className="font-display text-lg text-coal">What we'll prove in a week 1 sandbox test, before committing</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-[14px] min-w-[640px]" data-testid="sandbox-table">
              <thead>
                <tr>
                  {["What's illustrated", "Confidence", "If it doesn't work"].map((h) => (
                    <th key={h} className="py-2 text-left font-mono text-[10.5px] uppercase tracking-[0.05em] text-fog font-normal border-b border-hair">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SANDBOX.map(([what, lvl, label, fallback]) => (
                  <tr key={what} className="border-b border-hair last:border-0 align-top">
                    <td className="py-3 pr-4 text-coal/90">{what}</td>
                    <td className="py-3 pr-4"><span className={`font-mono text-[11px] rounded px-2 py-0.5 whitespace-nowrap ${LVL[lvl]}`}>{label}</span></td>
                    <td className="py-3 text-fog leading-relaxed">{fallback}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-4 bg-white border border-hair rounded-md p-5" data-testid="project-risks">
          <h3 className="font-display text-lg text-coal">Project risks</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-[14px] min-w-[680px]" data-testid="risks-table">
              <thead>
                <tr>
                  {["Risk", "Impact", "What we'd do"].map((h) => (
                    <th key={h} className="py-2 text-left font-mono text-[10.5px] uppercase tracking-[0.05em] text-fog font-normal border-b border-hair">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RISKS.map(([risk, note, lvl, label, doing]) => (
                  <tr key={risk} className="border-b border-hair last:border-0 align-top">
                    <td className="py-3 pr-4">
                      <b className="text-coal">{risk}</b>
                      {note && <br />}
                      {note && <span className="text-[12.5px] text-fog">{note}</span>}
                    </td>
                    <td className="py-3 pr-4"><span className={`font-mono text-[11px] rounded px-2 py-0.5 whitespace-nowrap ${LVL[lvl]}`}>{label}</span></td>
                    <td className="py-3 text-fog leading-relaxed">{doing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 border-l-[3px] border-warn pl-3.5 text-[14px] text-coal/85 leading-relaxed">
            If any of the week 1 tests don't work as expected, we'll come back to you
            before going further.
          </p>
        </div>
      </Reveal>
    </section>
  );
}
